import type { Rol } from '@condo/contracts';
import { AuthError, type AuthProvider, type AuthSession } from './auth-provider';

/**
 * Cognito Mock Provider.
 *
 * VITE_USE_MOCKS=true: simula usuarios demo con credenciales hardcoded.
 * El shape de `AuthSession` es el mismo que el real → se puede swappear
 * sin tocar componentes.
 *
 * Multi-tenant: cada usuario lleva un mapa `tenants` (tenantId → rol).
 * `maria@demo.cl` tiene membresías en dos condominios para ejercitar la
 * experiencia multi-tenant. `super@demo.cl` tiene `isSuperAdmin: true` y
 * `tenants: {}` (acceso global, no ligado a un tenant).
 */

export const MOCK_TENANT_ID = '01JBN5K7M3QWEABRS6P9ZCXV42';
export const MOCK_TENANT_ID_2 = '01KPCEYV9JVQCV1NRD7BWNNBDS';

const MOCK_SESSION_KEY = 'condo.mock.session';

export interface MockUser {
  password: string;
  email: string;
  name: string;
  isSuperAdmin: boolean;
  tenants: Record<string, string>;
  userId: string;
}

export const MOCK_USERS: Record<string, MockUser> = {
  'super@demo.cl': {
    password: 'demo',
    email: 'super@demo.cl',
    name: 'Consuelo Vergara',
    isSuperAdmin: true,
    tenants: {},
    userId: '01JBN5K7M3SUPER000000000001',
  },
  'admin@demo.cl': {
    password: 'demo',
    email: 'admin@demo.cl',
    name: 'Matias Olavarria',
    isSuperAdmin: false,
    tenants: { [MOCK_TENANT_ID]: 'admin' },
    userId: '01JBN5K7M3ADMIN000000000001',
  },
  'comite@demo.cl': {
    password: 'demo',
    email: 'comite@demo.cl',
    name: 'Javiera Pinto',
    isSuperAdmin: false,
    tenants: { [MOCK_TENANT_ID]: 'comite' },
    userId: '01JBN5K7M3COMITE00000000001',
  },
  'conserje@demo.cl': {
    password: 'demo',
    email: 'conserje@demo.cl',
    name: 'Pedro Gonzalez',
    isSuperAdmin: false,
    tenants: { [MOCK_TENANT_ID]: 'conserje' },
    userId: '01JBN5K7M3CONSR000000000001',
  },
  'propietario@demo.cl': {
    password: 'demo',
    email: 'propietario@demo.cl',
    name: 'Sofia Herrera',
    isSuperAdmin: false,
    tenants: { [MOCK_TENANT_ID]: 'propietario' },
    userId: '01JBN5K7M3PROP000000000001',
  },
  'residente@demo.cl': {
    password: 'demo',
    email: 'residente@demo.cl',
    name: 'Daniel Rivas',
    isSuperAdmin: false,
    tenants: { [MOCK_TENANT_ID]: 'residente' },
    userId: '01JBN5K7M3RES0000000000001',
  },
  'maria@demo.cl': {
    password: 'demo',
    email: 'maria@demo.cl',
    name: 'Maria Gonzalez',
    isSuperAdmin: false,
    tenants: { [MOCK_TENANT_ID]: 'admin', [MOCK_TENANT_ID_2]: 'residente' },
    userId: '01JBN5K7M3MARIA00000000001',
  },
};

/** Array version of MOCK_USERS for list rendering in the login page. */
export const MOCK_USERS_LIST = Object.values(MOCK_USERS);

/**
 * Builds a fake (non-signed) JWT-shaped token that encodes the multi-tenant
 * claims. MSW handlers can decode it with atob() to extract tenantId context.
 */
function buildMockToken(user: MockUser): string {
  const payload = {
    sub: user.userId,
    email: user.email,
    name: user.name,
    'cognito:groups': user.isSuperAdmin ? ['super_admin'] : [],
    'custom:tenants': JSON.stringify(user.tenants),
    exp: Math.floor(Date.now() / 1000) + 8 * 60 * 60,
  };
  return `mock.${btoa(JSON.stringify(payload))}.sig`;
}

export class MockAuthProvider implements AuthProvider {
  async signIn(email: string, password: string): Promise<AuthSession> {
    // Simulate network latency so loading states are visible.
    await new Promise((r) => setTimeout(r, 280));
    const user = MOCK_USERS[email.toLowerCase()];
    if (!user) throw new AuthError('user_not_found');
    if (user.password !== password) throw new AuthError('invalid_credentials');

    const session: AuthSession = {
      userId: user.userId,
      email: user.email,
      nombre: user.name,
      isSuperAdmin: user.isSuperAdmin,
      tenants: user.tenants as Record<string, Rol>,
      token: buildMockToken(user),
      expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
    };
    try {
      localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify(session));
    } catch {
      /* ignore storage errors */
    }
    return session;
  }

  async signOut(): Promise<void> {
    try {
      localStorage.removeItem(MOCK_SESSION_KEY);
    } catch {
      /* ignore */
    }
  }

  async restoreSession(): Promise<AuthSession | null> {
    try {
      const raw = localStorage.getItem(MOCK_SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as AuthSession;
      if (new Date(parsed.expiresAt).getTime() < Date.now()) {
        localStorage.removeItem(MOCK_SESSION_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }
}

/** @deprecated Use MOCK_TENANT_ID instead. Left for fixtures compatibility. */
export const DEMO_TENANT_ID = MOCK_TENANT_ID;
